import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Search, FlaskConical, Sprout } from "lucide-react";
import { Seo } from "../components/Seo";

const reveal = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.08 } }),
};

const exploring = [
  {
    icon: Search,
    title: "Find the Friction",
    desc: "Where do tasks stall? What gets entered twice? Which handoffs cause confusion, delays, or preventable mistakes? Understanding how work happens is the starting point for improving it.",
    accent: "violet",
  },
  {
    icon: FlaskConical,
    title: "Test Practical Improvements",
    desc: "A useful experiment begins with one clearly defined problem — gathering information, drafting documents, organizing work, or helping people make better-informed decisions. Start small. Test carefully. Measure what changes.",
    accent: "teal",
  },
  {
    icon: Sprout,
    title: "Help the Improvement Take Root",
    desc: "A promising tool needs clear responsibilities, trustworthy information, and people who understand how to use it. Training, communication, and accountability help improvements become part of everyday work.",
    accent: "violet",
  },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      <Seo
        title="SymbiCognitive | Practical AI, People & Better Operations"
        description="Exploring how Wisconsin service businesses can improve everyday work through practical AI, thoughtful leadership, and stronger processes."
        path="/"
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-midnight pt-20" data-testid="hero-section">
        <div
          className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1684503830683-108f3e0fd03f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')" }}
        />
        <div className="absolute inset-0 bg-midnight/85" />
        <div
          className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(139,92,246,0.20), rgba(0,180,216,0.09) 45%, transparent 70%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-20 sm:pt-28 sm:pb-28">
          <div className="max-w-3xl">
            <motion.span variants={reveal} initial="hidden" animate="show" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-teal font-semibold mb-6">
              <span className="h-px w-8 bg-teal" /> Practical AI &amp; Better Operations · Wisconsin
            </motion.span>
            <motion.h1 variants={reveal} custom={1} initial="hidden" animate="show" className="font-display font-extrabold text-white text-4xl sm:text-6xl lg:text-[4.2rem] leading-[1.03] tracking-tight">
              Your best people have better things to do than chase paperwork.
            </motion.h1>
            <motion.p variants={reveal} custom={2} initial="hidden" animate="show" className="mt-7 text-lg text-slate-300 max-w-2xl leading-relaxed">
              Quotes, schedules, customer follow-ups, and repetitive administrative work can swallow an astonishing amount of time. SymbiCognitive is exploring how practical AI, thoughtful leadership, and better processes can help Wisconsin service businesses work more effectively — and help their people thrive.
            </motion.p>
            <motion.div variants={reveal} custom={3} initial="hidden" animate="show" className="mt-9 flex flex-wrap gap-4">
              <Link to="/field-notes" data-testid="hero-primary-cta" className="group inline-flex items-center gap-2 bg-amber text-midnight font-bold px-7 py-3.5 rounded-full glow-amber">
                Read the Field Notes
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/our-approach" data-testid="hero-secondary-cta" className="inline-flex items-center gap-2 text-white font-semibold px-7 py-3.5 rounded-full border border-white/20 hover:border-teal hover:text-teal transition-colors">
                Our Approach
              </Link>
            </motion.div>
            <motion.p variants={reveal} custom={4} initial="hidden" animate="show" className="mt-6 text-sm text-slate-400" data-testid="dev-stage-note">
              SymbiCognitive is a developing project exploring practical AI adoption in smaller organizations.
            </motion.p>
          </div>
        </div>
        <div className="gradient-divider h-1.5 w-full" data-testid="hero-divider" />
      </section>

      {/* EVERYDAY WORK */}
      <section className="bg-white py-24 sm:py-32" data-testid="everyday-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">The everyday work is the opportunity</span>
              <h2 className="font-display font-bold text-slateink text-3xl sm:text-4xl tracking-tight mt-4 leading-tight">Sound familiar?</h2>
            </div>
            <div className="lg:col-span-7 space-y-6 text-[var(--slate-soft)] text-lg leading-relaxed">
              <p>A customer calls for a quote. The details are scattered across emails, notes, and spreadsheets. Someone tracks down the missing information, someone else prepares the estimate, and another person follows up. Meanwhile, the phone keeps ringing.</p>
              <p>For a service business with 10 to 50 employees, small inefficiencies add up. They consume time, complicate communication, frustrate customers, and pull talented people away from more valuable work.</p>
              <p>Artificial intelligence offers intriguing possibilities. Finding the opportunities that make sense for a particular business takes a little more care. Where could a useful tool eliminate repetitive steps? Which processes need attention first? How do we help employees use new technology confidently?</p>
              <p className="text-slateink font-medium">Those are the questions behind SymbiCognitive.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHAT WE'RE EXPLORING */}
      <section className="bg-white pb-24 sm:pb-32" data-testid="exploring-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="border-t border-slate-200 pt-20">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">What we're exploring</span>
            <h2 className="font-display font-bold text-slateink text-3xl sm:text-4xl tracking-tight mt-4 max-w-2xl leading-tight">Better ways of working begin with understanding the work.</h2>
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              {exploring.map((s, i) => {
                const Icon = s.icon;
                const isViolet = s.accent === "violet";
                return (
                  <motion.div key={s.title} variants={reveal} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} data-testid={`exploring-card-${i}`} className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-slate-300/50 hover:border-transparent">
                    <span className={`absolute left-0 top-0 h-full w-1 origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ${isViolet ? "bg-violet" : "bg-teal"}`} />
                    <div className={`h-14 w-14 rounded-xl grid place-items-center transition-colors duration-300 ${isViolet ? "bg-violet/10 text-violet group-hover:bg-violet group-hover:text-white" : "bg-teal/10 text-teal group-hover:bg-teal group-hover:text-white"}`}>
                      <Icon size={26} />
                    </div>
                    <h3 className="mt-6 font-display font-semibold text-slateink text-xl leading-snug">{s.title}</h3>
                    <p className="mt-3 text-[var(--slate-soft)] leading-relaxed">{s.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY TEASER */}
      <section className="bg-midnight py-24 sm:py-28 relative overflow-hidden" data-testid="philosophy-teaser">
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(0,180,216,0.14), transparent 65%)" }} />
        <div className="max-w-3xl mx-auto px-6 sm:px-10 relative text-center">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-teal">Our philosophy</span>
          <h2 className="font-display font-bold text-white text-3xl sm:text-5xl tracking-tight mt-4">Roots before fruits.</h2>
          <p className="mt-6 text-lg text-slate-300 leading-relaxed">
            When it matters to you, you build it to last. A useful AI tool needs dependable information, thoughtful oversight, capable people, and a process that makes sense. Each improvement gives us a chance to learn, strengthen the work, and build on what succeeds.
          </p>
          <Link to="/our-approach" data-testid="philosophy-cta" className="group mt-8 inline-flex items-center gap-2 text-amber font-semibold">
            Read Our Approach
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
